import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import IconRisk from '../icon/icon-risk';
import IconResilience from '../icon/icon-resilience';
import styles from './HalfCircleChart.module.scss';

// Register only the components we need
echarts.use([PieChart, SVGRenderer]);

const HalfCircleChart = ({ riskScore, resilienceScore, riskCategory, resilienceCategory }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current, null, { renderer: 'svg' });

    // Calculate radius based on percentage (0-100% maps to 0-90% of available space)
    const riskRadius = `${(riskScore / 100) * 90}%`;
    const resilienceRadius = `${(resilienceScore / 100) * 90}%`;

    const option = {
      series: [
        // Reference circle outline (100%)
        {
          type: 'pie',
          radius: ['89%', '90%'],
          center: ['50%', '50%'],
          startAngle: 90,
          data: [{ value: 100 }],
          itemStyle: {
            color: '#405363'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Ring at 80%
        {
          type: 'pie',
          radius: ['71%', '72%'],
          center: ['50%', '50%'],
          startAngle: 90,
          data: [{ value: 100 }],
          itemStyle: {
            color: '#5F6E7B'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Ring at 60%
        {
          type: 'pie',
          radius: ['53%', '54%'],
          center: ['50%', '50%'],
          startAngle: 90,
          data: [{ value: 100 }],
          itemStyle: {
            color: '#7D8992'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Ring at 40%
        {
          type: 'pie',
          radius: ['35%', '36%'],
          center: ['50%', '50%'],
          startAngle: 90,
          data: [{ value: 100 }],
          itemStyle: {
            color: '#9CA3AA'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Ring at 20%
        {
          type: 'pie',
          radius: ['17%', '18%'],
          center: ['50%', '50%'],
          startAngle: 90,
          data: [{ value: 100 }],
          itemStyle: {
            color: '#BABEC1'
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Risk - Left semicircle (Red)
        {
          type: 'pie',
          radius: [0, riskRadius],
          center: ['50%', '50%'],
          startAngle: 270,
          endAngle: 90,
          data: [{ value: 1 }],
          itemStyle: {
            color: 'rgba(146, 0, 0, 0.60)'  // Red with transparency
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        },
        // Resilience - Right semicircle (Green)
        {
          type: 'pie',
          radius: [0, resilienceRadius],
          center: ['50%', '50%'],
          startAngle: 90,
          endAngle: 270,
          data: [{ value: 1 }],
          itemStyle: {
            color: 'rgba(0, 139, 37, 0.60)'  // Green with transparency
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          silent: true
        }
      ]
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [riskScore, resilienceScore]);

  return (
    <div className={styles.halfCircleChart}>
      <div className={`${styles.chartLabels} row justify-content-between align-items-end`}>
        <div className={`${styles.labelLeft} col-6 text-right`}>
          <IconRisk />
          <div className={styles.labelText}>Risk</div>
        </div>
        <div className={`${styles.labelRight} col-6 text-left`}>
          <IconResilience />
          <div className={styles.labelText}>Resilience</div>
        </div>
      </div>
      <div className={styles.chartContainer} ref={chartRef}></div>
      <div className={styles.scoreContainer}>
          <div className="row justify-content-between align-items-end">
              <div className="col-6">
                  <div className={`${styles.scoreItem} text-right`}>
                      <div className={styles.scoreLabel}>Total<br />Risk Score<br />(Out of 100,<br /> Lower is Better)</div>
                      <div className={`${styles.scoreValue} ${styles.riskValue}`}>{riskScore.toFixed(1)}</div>
                  </div>
              </div>
              <div className="col-6">
                  <div className={styles.scoreItem}>
                      <div className={styles.scoreLabel}>Total<br />Resilience Score<br /> (Out of 100,<br /> Higher is better)</div>
                      <div className={`${styles.scoreValue} ${styles.resilienceValue}`}>{resilienceScore.toFixed(2)}</div>
                  </div>
              </div>
          </div>
          <div className="row align-items-end">
              <div className="col-6">
                  <div className={`${styles.scoreItem} text-right`}>
                      <div className={styles.scoreLabel}>Risk Category</div>
                      <div className={`${styles.scoreValue} ${styles.riskValue}`}>{riskCategory}</div>
                  </div>
              </div>
              <div className="col-6">
                  <div className={styles.scoreItem}>
                      <div className={styles.scoreLabel}>Resilience<br />Category</div>
                      <div className={`${styles.scoreValue} ${styles.resilienceValue}`}>{resilienceCategory}</div>
                  </div>
              </div>
          </div>
      </div>
    </div>
  );
};

export default HalfCircleChart;